import React, { createContext, useContext, useState, useEffect } from 'react';
import { initKeycloak, getToken, getUserInfo, isAuthenticated, logout, login, getKeycloakInstance, hasAdminRole } from '../services/keycloakService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      if (initialized) return; // Prevent multiple initialization attempts
      
      try {
        console.log('Initializing Keycloak...');
        const keycloak = getKeycloakInstance();
        
        const authResult = await initKeycloak();
        console.log('Keycloak initialization result:', authResult);
        
        setInitialized(true);
        
        if (isAuthenticated()) {
          console.log('User is authenticated');
          const userInfo = getUserInfo();
          const adminRole = hasAdminRole();
          
          console.log('User info:', userInfo);
          console.log('Has admin role:', adminRole);
          
          setUser(userInfo);
          setAuthenticated(true);
          setIsAdmin(adminRole);
          
          // If user doesn't have admin role, show error and logout
          if (!adminRole) {
            console.error('User does not have admin role');
            alert('Access denied. Admin role required to access this portal.');
            handleLogout();
            return;
          }
          
          // Set up token refresh
          const refreshInterval = setInterval(() => {
            keycloak.updateToken(30).catch((error) => {
              console.error('Failed to refresh token:', error);
              clearInterval(refreshInterval);
              handleLogout();
            });
          }, 60000); // Check every minute
          
          // Cleanup interval on unmount
          return () => clearInterval(refreshInterval);
        } else {
          console.log('User is not authenticated');
          setAuthenticated(false);
        }
      } catch (error) {
        console.error('Failed to initialize Keycloak:', error);
        setInitialized(true);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [initialized]);

  const handleLogout = () => {
    setUser(null);
    setAuthenticated(false);
    logout();
  };

  const handleLogin = () => {
    return login();
  };

  const value = {
    user,
    authenticated,
    isAdmin,
    loading,
    logout: handleLogout,
    login: handleLogin,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};